import { Controller } from "@hotwired/stimulus";
import { loadStripe } from "@stripe/stripe-js";

export default class extends Controller {
  static values = {
    publishableKey: String,
    saleSecret: String,
    currencyId: String,
    userLoggedIn: Boolean,
    successPath: String,
  };

  static targets = [
    "form",
    "cardNumber",
    "cardExpiry",
    "cardCvc",
    "cardNumberErrors",
    "cardExpiryErrors",
    "cardCvcErrors",
    "submitButton",
    "submitText",
    "loadingText",
  ];

  async connect() {
    this.stripe = null;
    this.elements = null;
    this.cardNumberElement = null;
    this.cardExpiryElement = null;
    this.cardCvcElement = null;
    this.formLoading = false;

    // Bind form submit handler
    if (this.hasFormTarget) {
      this.formTarget.addEventListener("submit", (e) => this.submit(e));
    }

    await this.initializeStripe();
  }

  async initializeStripe() {
    try {
      // Load Stripe using @stripe/stripe-js
      this.stripe = await loadStripe(this.publishableKeyValue);

      if (!this.stripe) {
        console.error("Error al cargar Stripe.js");
        return;
      }

      this.elements = this.stripe.elements();

      const style = {
        base: {
          fontSize: "16px",
        },
      };

      // Create card number element
      this.cardNumberElement = this.elements.create("cardNumber", {
        style: style,
      });
      this.cardNumberElement.mount(this.cardNumberTarget);
      this.cardNumberElement.on("change", (event) => {
        this.handleElementChange(event, this.cardNumberErrorsTarget);
      });

      // Create card expiry element
      this.cardExpiryElement = this.elements.create("cardExpiry", {
        style: style,
      });
      this.cardExpiryElement.mount(this.cardExpiryTarget);
      this.cardExpiryElement.on("change", (event) => {
        this.handleElementChange(event, this.cardExpiryErrorsTarget);
      });

      // Create card CVC element
      this.cardCvcElement = this.elements.create("cardCvc", {
        style: style,
      });
      this.cardCvcElement.mount(this.cardCvcTarget);
      this.cardCvcElement.on("change", (event) => {
        this.handleElementChange(event, this.cardCvcErrorsTarget);
      });

      // Enable submit button once Stripe is initialized
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false;
      }
    } catch (error) {
      console.error(
        "[StripePaymentController] Error initializing Stripe:",
        error
      );
      this.showError("Error al inicializar el sistema de pagos");
    }
  }

  handleElementChange(event, errorTarget) {
    if (event.error) {
      errorTarget.textContent = event.error.message;
      errorTarget.classList.remove("hidden");
    } else {
      errorTarget.classList.add("hidden");
    }
  }

  async submit(event) {
    // Prevenir el submit del formulario de Stripe
    // El botón principal orquesta todo el flujo
    event.preventDefault();
    event.stopPropagation();
  }

  async createPaymentMethod() {
    if (this.formLoading || !this.stripe || !this.elements) {
      return false;
    }

    this.setLoading(true);

    try {
      const { error, paymentMethod } = await this.stripe.createPaymentMethod({
        type: 'card',
        card: this.cardNumberElement,
        billing_details: {
          name: this.getBillingName(),
        }
      });

      if (error) {
        this.handleError(error);
        this.setLoading(false);
        return false;
      }

      if (!paymentMethod) {
        this.showError("Error al procesar la tarjeta");
        this.setLoading(false);
        return false;
      }

      this.savePaymentMethodToForm(paymentMethod);
      this.setLoading(false);
      return true;
    } catch (error) {
      console.error("[StripePaymentController] Error:", error);
      this.showError("Error al validar la tarjeta. Por favor, intenta nuevamente.");
      this.setLoading(false);
      return false;
    }
  }

  savePaymentMethodToForm(paymentMethod) {
    const checkoutForm = document.querySelector('form[action*="checkout_form"]');

    if (!checkoutForm) {
      console.error("No se encontró el formulario de checkout");
      return;
    }

    const pmData = {
      id: paymentMethod.id,
      card_id: paymentMethod.card.id,
      funding: paymentMethod.card.funding,
      last4: paymentMethod.card.last4,
      brand: paymentMethod.card.brand,
    };

    Object.keys(pmData).forEach((key) => {
      const fieldName = `showcase_checkout_checkout_form[sale_params][stripe_token][${key}]`;
      let input = checkoutForm.querySelector(`input[name="${fieldName}"]`);

      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = fieldName;
        checkoutForm.appendChild(input);
      }

      input.value = pmData[key];
    });
  }

  getBillingName() {
    const firstNameInput = document.querySelector('input[name*="[first_name]"]');
    const lastNameInput = document.querySelector('input[name*="[last_name]"]');

    const firstName = firstNameInput?.value || '';
    const lastName = lastNameInput?.value || '';

    return `${firstName} ${lastName}`.trim();
  }

  // Método público para validar Stripe y crear el token
  // Solo valida los datos de la tarjeta y crea el token
  // El backend decidirá si guarda la tarjeta o no
  async validateAndCreateCard() {
    if (this.formLoading || !this.stripe || !this.elements) {
      return false;
    }

    this.setLoading(true);

    try {
      // Validar Stripe - crear token de la tarjeta
      const { error, token } = await this.stripe.createToken(
        this.cardNumberElement
      );

      if (error) {
        this.handleError(error);
        this.setLoading(false);
        return false;
      }

      if (!token) {
        this.showError("Error al generar el token de la tarjeta");
        this.setLoading(false);
        return false;
      }

      // Guardar el token en el formulario principal para que se envíe con el submit
      this.saveTokenToForm(token);

      this.setLoading(false);
      return true;
    } catch (error) {
      console.error("[StripePaymentController] Error:", error);
      this.showError(
        "Error al validar la tarjeta. Por favor, intenta nuevamente."
      );
      this.setLoading(false);
      return false;
    }
  }

  // Guarda el token de Stripe en el formulario principal
  saveTokenToForm(token) {
    const checkoutForm = document.querySelector(
      'form[action*="checkout_form"]'
    );

    if (!checkoutForm) {
      console.error("No se encontró el formulario de checkout");
      return;
    }

    // Crear o actualizar campos hidden con la información del token
    const tokenData = {
      id: token.id,
      card_id: token.card.id,
      funding: token.card.funding,
      last4: token.card.last4,
    };

    // Guardar en el formulario como campos hidden
    Object.keys(tokenData).forEach((key) => {
      const fieldName = `showcase_checkout_checkout_form[sale_params][stripe_token][${key}]`;
      let input = checkoutForm.querySelector(`input[name="${fieldName}"]`);

      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = fieldName;
        checkoutForm.appendChild(input);
      }

      input.value = tokenData[key];
    });
  }

  handleError(error) {
    let errorTarget = null;
    if (error.code === "incomplete_number") {
      errorTarget = this.cardNumberErrorsTarget;
    } else if (error.code === "incomplete_expiry") {
      errorTarget = this.cardExpiryErrorsTarget;
    } else if (error.code === "incomplete_cvc") {
      errorTarget = this.cardCvcErrorsTarget;
    }

    if (errorTarget) {
      errorTarget.textContent = error.message;
      errorTarget.classList.remove("hidden");
    } else {
      this.showError(error.message);
    }
  }

  showError(message) {
    // Show error in a general error area or use a flash message
    alert(message);
  }

  setLoading(loading) {
    this.formLoading = loading;
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = loading;
    }
    if (this.hasSubmitTextTarget && this.hasLoadingTextTarget) {
      if (loading) {
        this.submitTextTarget.classList.add("hidden");
        this.loadingTextTarget.classList.remove("hidden");
      } else {
        this.submitTextTarget.classList.remove("hidden");
        this.loadingTextTarget.classList.add("hidden");
      }
    }
  }

  disconnect() {
    if (this.cardNumberElement) {
      this.cardNumberElement.unmount();
    }
    if (this.cardExpiryElement) {
      this.cardExpiryElement.unmount();
    }
    if (this.cardCvcElement) {
      this.cardCvcElement.unmount();
    }
  }
}
